import React from "react";
import PropTypes from "prop-types";
import { Row, Col, FormGroup, Label, Input, Button } from "reactstrap";
import { Link } from "react-router-dom";
import PaymentMethodBadges from "../../ShopProduct/utils/PaymentMethodBadges";
import PerksSmall from "../../PerksSmall";
import shopProductSelectors from "../../../redux/shop/products/selectors";
import FormattedNumber from "../../FormattedNumber";
import { useSelector } from "react-redux";

const PurchaseButton = (
  {
    productId,
    configuratorPage,
    updateAmount,
    amount,
    currentVariant,
    addCustomItemToCart,
    handleAddItemToCart,
    scrollToConfigurator,
    expressDeliveryDays,
    standardDeliveryDays,
    skipdc,
    getCustomPrice,
    scrollToSection,
  },
  context
) => {
  // Fetch the language from the Redux store
  const language = useSelector((state) => state.i18nState.lang);

  const shopProduct = useSelector((state) =>
    shopProductSelectors.selectProductById(state, language, productId)
  );

  const shopProductData = {
    attributes: shopProduct?.data?.attributes,
    included: shopProduct?.included,
  };

  const isFixedAmount = [18, 19, 20].includes(
    parseInt(shopProductData.attributes?.myfolie_configurator_id)
  );

  const renderAmountInput = () => (
    <FormGroup>
      <Label for="amount">{context.t("amount")}</Label>
      <Input
        min="1"
        onChange={updateAmount}
        value={isFixedAmount ? 50 : amount}
        placeholder={context.t("amount")}
        style={{ textAlign: "center" }}
        className="mr-1 no-spin-buttons data-hj-allow"
        type="number"
        step="1"
        disabled={isFixedAmount}
      />
    </FormGroup>
  );

  const renderPriceDetails = () => {
    const hasCustomSize = shopProductData.included?.find(
      (obj) =>
        obj.type === "option_type" && obj.attributes.name === "custom_size"
    );
    const customPrice = hasCustomSize ? getCustomPrice() : 0;

    return (
      <>
        <div className="h5 mt-4 pt-2">
          {hasCustomSize ? (
            customPrice > 0 ? (
              <>
                <FormattedNumber
                  value={parseFloat(customPrice)}
                  language={language}
                  unit="€"
                  decimals={2}
                />{" "}
                / {context.t("piece")}
              </>
            ) : (
              <>
                {context.t("from")}{" "}
                <FormattedNumber
                  value={parseFloat(currentVariant.attributes.price)}
                  language={language}
                  unit="€"
                  decimals={2}
                />{" "}
                / {context.t("piece")}
              </>
            )
          ) : (
            <>
              {shopProductData.attributes?.customizable && (
                <>{context.t("from")} </>
              )}
              <FormattedNumber
                value={parseFloat(currentVariant.attributes.price)}
                language={language}
                unit="€"
                decimals={2}
              />{" "}
              / {context.t("piece")}
            </>
          )}
        </div>
        <div className="small text-hint">
          {context.t("incl. VAT")} {context.t("plus shipping")}
        </div>
      </>
    );
  };

  const renderAddToCartButtons = () => {
    const hasCustomOptions = shopProductData.included?.find(
      (obj) =>
        obj.type === "option_type" &&
        (obj.attributes.name === "custom_size" ||
          obj.attributes.name === "custom_colorselect")
    );

    if (!shopProductData.attributes.customizable && !hasCustomOptions) {
      return (
        <Button
          className="mt-1"
          block
          size="lg"
          color="success"
          onClick={handleAddItemToCart}
        >
          {context.t("Add to Cart")}
        </Button>
      );
    }

    if (!shopProductData.attributes.customizable && hasCustomOptions) {
      return (
        <>
          <Button
            className="mt-1"
            block
            size="lg"
            color="success"
            onClick={addCustomItemToCart}
          >
            {context.t("Add to Cart with your configuration")}
          </Button>
        </>
      );
    }

    if (shopProductData.attributes.customizable) {
      const configuratorId = parseInt(
        shopProductData.attributes.myfolie_configurator_id
      );
      const linkTo =
        configuratorId !== 6
          ? `/${language}/eigenes-motiv/?product=${configuratorId}&amount=${
              isFixedAmount ? "50&width=25&height=25" : amount
            }${skipdc ? "&skipdc=true" : ""}`
          : `/${language}/klebeschriften/?amount=${amount}`;

      return (
        <Link className="mt-1 btn btn-success btn-lg btn-block" to={linkTo}>
          {context.t("Customize")}
        </Link>
      );
    }

    return null;
  };

  const renderDeliveryInfo = () => (
    <span className="text-success">
      {context.t("delivery time")}: Express:{" "}
      {(amount > 50 ? 1 : 0) + expressDeliveryDays - 2}-
      {(amount > 50 ? 1 : 0) + expressDeliveryDays} {context.t("days")}*,
      Standard {Math.ceil((amount > 50 ? 2 : 0) + standardDeliveryDays / 2)}-
      {(amount > 50 ? 1 : 0) + standardDeliveryDays} {context.t("days")}*
    </span>
  );

  return (
    <>
      {!configuratorPage && (
        <Row>
          <Col xs={5}>{renderAmountInput()}</Col>
          <Col xs={7}>
            {shopProductData.included ? (
              renderPriceDetails()
            ) : (
              <div className="sk-loading sk-line bg-light" />
            )}
          </Col>
        </Row>
      )}

      {!configuratorPage && shopProductData.included && (
        <>
          {renderAddToCartButtons()}
          <PaymentMethodBadges />
        </>
      )}

      {!shopProductData.included && (
        <div
          className="sk-loading btn btn-block mt-4 sk-bg300"
          style={{ height: "40px" }}
        />
      )}

      {configuratorPage && shopProductData.included && (
        <>
          {renderPriceDetails()}
          <Button
            className="mt-1 btn btn-success btn-block"
            size="lg"
            color="success"
            onClick={scrollToConfigurator}
          >
            {context.t("configure now")}
          </Button>
          <PaymentMethodBadges />
        </>
      )}

      {renderDeliveryInfo()}

      <p style={{ whiteSpace: "pre-line" }} className="pt-4">
        <span
          dangerouslySetInnerHTML={{
            __html: shopProductData.attributes.short_description,
          }}
        />
        {configuratorPage && (
          <>
            <br />
            <span
              className="btn btn-link"
              onClick={() => scrollToSection("description")}
            >
              {context.t("Details")}
            </span>
          </>
        )}
      </p>

      {(configuratorPage || shopProductData.attributes.customizable) && (
        <PerksSmall />
      )}
    </>
  );
};

/** Define proptype for the 'translation' function */
PurchaseButton.contextTypes = {
  t: PropTypes.func,
};

export default PurchaseButton;
